import React from 'react'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import TextArea from './TextArea'
import Actions from './Actions'
import AttachedFile from '../../../atoms/AttachedFile'
import SendControl from '../../../molecules/SendControl'

import { postTask, uploadFileTask } from '@SonarCX/lib/api'
import { parseTags } from '@SonarCX/utilities'
import { dehydrateTask } from '@SonarCX/providers/TaskAPIProvider/helpers'
import useAccess, { PERMS } from '@SonarCX/hooks/useAccess'
import { useAgent } from '@SonarCX/providers/AgentProvider'
import { fetchTags } from '@SonarCX/slices/tag'
import {
  selectChatControls,
  updateValue,
  resetTask,
  clearData
} from '@SonarCX/slices/chatControlsSlice'
import { useMediaLoaded } from '@SonarCX/providers/MediaLoadedProvider'
import { useSocket } from '@SonarCX/providers/SocketProvider'
import { useChatEditState } from '@SonarCX/providers/ChatEditStateProvider'
import { useOnNotes } from '@SonarCX/providers/OnNotesProvider'
import { useOwner } from '@SonarCX/providers/OwnerProvider'
import { useEnterMode } from '@SonarCX/providers/EnterModeProvider'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import { useTaskAPI } from '@SonarCX/providers/TaskAPIProvider'
import { useVerification } from '@SonarCX/providers/VerificationProvider'
import { transformToEmoji } from '@SonarCX/utilities/stringToEmoji'
import { AddErrorAlert } from '@SonarCX/slices/alert'
import { selectDMs } from '@SonarCX/slices/dms'
import useStyles from './styles'

const SPACE_KEYCODE = 32
const UNDO_KEYCODE = 90

const Controls = ({
  convo,
  onTypeSet,
  isDirect,
  agents,
  onMessageSubmit,
  onCommentSubmit,
  session,
  onTyping,
  scrollToBottom,
  startRecording,
  stopRecording,
  recordingBlob,
  isRecording,
  recordingTime,
  clearRecord
}) => {
  const dispatch = useAppDispatch()
  const tagsSelector = useAppSelector(storeState => storeState.tag)
  const { tags } = tagsSelector
  const {
    message,
    email,
    subject,
    file,
    phoneNumber,
    taskVals,
    undoText,
    isReplacedString,
    hasUndoReplacedString
  } = useAppSelector(selectChatControls)

  const { currentUser } = useAgent()
  const { doneUploading } = useMediaLoaded()
  const {
    dashboard: { error: dashboardError }
  } = useSocket()
  const {
    state: editState,
    start: startEdit,
    update: updateEdit,
    end: endEdit,
    submit: submitEdit
  } = useChatEditState()
  const { onNotes: allOnNotes, showNotes, hideNotes } = useOnNotes()
  const hasPermission = useAccess()
  const { deleted: ownerDeleted } = useOwner()
  const { sendOnEnter, temp, enableTemp, resetTemp } = useEnterMode()
  const { pushTask } = useTaskAPI()
  const { isSmall } = useBreakpoint()
  const { verified } = useVerification() ?? { verified: true }
  const { selectedConvo: id } = useAppSelector(selectDMs)
  const [sent, setSent] = React.useState(false)

  const onNotes = React.useMemo(
    () => Boolean(allOnNotes[session]),
    [allOnNotes, session]
  )

  const allTags = React.useMemo(() => [...new Set(tags)], [tags])

  const canAddNotes = React.useMemo(
    () => hasPermission(PERMS.NotesFull),
    [hasPermission]
  )

  const classes = useStyles({ smallMedia: isSmall, verified })

  const { type, users: sessionUsers } = convo

  const isYours =
    isDirect ||
    sessionUsers?.includes(currentUser()?.id) ||
    sessionStorage.getItem('viewing') === session
  const disabled = !isYours || convo.closed

  const setMessage = React.useCallback(
    val => {
      dispatch(updateValue('message', val))
    },
    [dispatch]
  )

  const setFile = React.useCallback(
    val => {
      dispatch(updateValue('file', val))
    },
    [dispatch]
  )

  const setTaskVals = React.useCallback(
    val => {
      dispatch(updateValue('taskVals', val))
    },
    [dispatch]
  )

  const setUndoText = React.useCallback(
    val => {
      dispatch(updateValue('undoText', val))
    },
    [dispatch]
  )

  const setIsReplacedString = React.useCallback(
    val => {
      dispatch(updateValue('isReplacedString', val))
    },
    [dispatch]
  )

  const setHasUndoReplacedString = React.useCallback(
    val => {
      dispatch(updateValue('hasUndoReplacedString', val))
    },
    [dispatch]
  )

  React.useEffect(() => {
    if (!tagsSelector.loaded && !tagsSelector.loading) {
      dispatch(fetchTags())
    }
  }, [tagsSelector, dispatch])

  // resetting message box when convo changes
  // also resets editing state when changing convos
  React.useEffect(() => {
    endEdit()
  }, [id, endEdit])

  // Switch to notes when convo is closed
  React.useEffect(() => {
    if (convo?.closed && !onNotes) {
      showNotes(convo.session)
    }
  }, [convo, onNotes, showNotes])

  // Creates / handles created tasks, either from modal or inline
  const createTask = React.useCallback(
    async created => {
      if (created) {
        dispatch(resetTask())
        pushTask(created)
        return
      }

      const { title, content, file } = taskVals
      if (title.trim().length === 0) {
        dispatch(
          AddErrorAlert({ err: { message: 'Title must not be empty.' } })
        )
      }

      try {
        let media_ids = []
        if (file) {
          const media_res = await uploadFileTask(file)
          if (media_res) {
            media_ids = [media_res.id]
          }
        }

        const { primary, date, status, category, priority, type, watchers } =
          taskVals

        const assignee_ids = primary ? [primary] : []
        const [body, tags] = parseTags(content)

        // check which user is concerning_user_id from users array in convo if there is no owner
        let agentIds = []
        agents.forEach(agent => agentIds.push(agent.id))
        const concerning_user = convo.users.filter(
          user => !agentIds.includes(user) && currentUser()?.id !== user
        )

        const data = {
          title,
          details: body,
          parent_id: null,
          due_date: date,
          status_id: status,
          type_id: type,
          tags,
          assignee_ids,
          media_ids,
          primary_assignee_id: primary || null,
          session_ids: session ? [session] : [],
          watcher_ids: watchers,
          category_id: category ?? null,
          priority_id: priority ?? null,
          concerning_user_id: convo.owner_id ?? concerning_user[0] ?? null
        }

        try {
          const task = await postTask(dehydrateTask(data))
          dispatch(resetTask())
          pushTask(task)
          scrollToBottom()
        } catch (err) {
          console.error('Post error for task in Controls', err)
          dispatch(AddErrorAlert({ err }))
        }
      } catch (err) {
        console.error('Error on file upload in Controls', err)
        dispatch(AddErrorAlert({ err }))
      }
    },
    [
      pushTask,
      session,
      taskVals,
      scrollToBottom,
      agents,
      convo.users,
      currentUser,
      convo.owner_id,
      dispatch
    ]
  )

  // Sends a message through the socket
  const sendMessage = React.useCallback(
    (close = false, clicked) => {
      const { newText } = transformToEmoji(message)
      let value = (hasUndoReplacedString ? message.trim() : newText).trim()
      if (value.length > 0 || file.length > 0) {
        const mode = close ? 'Send as Closed' : 'Send'
        resetTemp()
        if (!onNotes) {
          let message = value
          if (type === 'SMS') {
            message = { content: value, number: phoneNumber }
          } else if (type === 'Email') {
            message = { content: value, title: subject, email: email }
          }
          onMessageSubmit(message, mode, file)
          setSent(true)
        } else {
          if (clicked) {
            setMessage('')
            onCommentSubmit(value, mode, file)
            setSent(true)
            hideNotes(session)
            setFile([])
          }
        }
        setHasUndoReplacedString(false)
      }
    },
    [
      message,
      setMessage,
      type,
      email,
      file,
      setFile,
      onCommentSubmit,
      onMessageSubmit,
      onNotes,
      hideNotes,
      phoneNumber,
      subject,
      session,
      resetTemp,
      hasUndoReplacedString,
      setHasUndoReplacedString
    ]
  )

  React.useEffect(() => {
    // clear message after data is uploaded
    if (!dashboardError && sent) {
      setSent(false)
      dispatch(clearData())
      setFile([])
    }
  }, [dispatch, dashboardError, setFile, sent])

  // responding to files growing text area and msg staying on bottom
  React.useEffect(() => {
    if (file?.length) {
      scrollToBottom()
    }
  }, [file, scrollToBottom])

  // Sends message if enter is pressed
  const checkSubmit = React.useCallback(
    e => {
      const { key, shiftKey, keyCode } = e

      if (key === 'Enter') {
        // Disable send on enter when shift is held once
        if (shiftKey) {
          enableTemp()
          return
        }

        // Prevent sending when disabled
        if (!sendOnEnter || temp || isRecording) {
          return
        }

        // Emails should never send from key input
        const shouldSend = type !== 'Email' && doneUploading

        if (shouldSend) {
          if (editState.editing) {
            e.preventDefault()
            submitEdit()
            resetTemp()
          } else {
            sendMessage()
          }
        }
      } else {
        if (keyCode === SPACE_KEYCODE) {
          setHasUndoReplacedString(false)
          if (type === 'Task') {
            const { newText, originalText, replaced } = transformToEmoji(
              taskVals.content
            )
            setUndoText(originalText)
            setIsReplacedString(replaced)
            setTaskVals(vals => ({ ...vals, content: newText }))
          } else if (editState.editing) {
            const { newText, originalText, replaced } = transformToEmoji(
              editState.newMessage
            )
            setUndoText(originalText)
            setIsReplacedString(replaced)
            updateEdit(newText)
          } else {
            const { newText, originalText, replaced } =
              transformToEmoji(message)
            setUndoText(originalText)
            setIsReplacedString(replaced)
            setMessage(newText)
          }
        }
        if (
          keyCode === UNDO_KEYCODE &&
          (e.ctrlKey || e.metaKey) &&
          isReplacedString
        ) {
          if (type === 'Task') {
            setTaskVals(vals => ({ ...vals, content: undoText }))
          } else if (editState.editing) {
            updateEdit(undoText)
          } else {
            setMessage(undoText)
          }
          setHasUndoReplacedString(true)
          e.preventDefault()
        }
        if (key === 'ArrowUp') {
          if (message.trim().length === 0 && !editState.editing) {
            startEdit()
            e.preventDefault()
          }
        } else if (key === 'Escape') {
          endEdit()
          e.preventDefault()
        }
      }

      onTyping()
    },
    [
      sendMessage,
      onTyping,
      doneUploading,
      type,
      endEdit,
      startEdit,
      submitEdit,
      enableTemp,
      sendOnEnter,
      temp,
      resetTemp,
      message,
      isReplacedString,
      setIsReplacedString,
      setMessage,
      setUndoText,
      undoText,
      setTaskVals,
      taskVals,
      setHasUndoReplacedString,
      editState,
      updateEdit,
      isRecording
    ]
  )

  const textAreaProps = React.useMemo(
    () => ({
      type,
      onTypeSet,
      isDirect,
      checkSubmit,
      disabled: disabled || ownerDeleted,
      tags: allTags,
      createTask,
      session,
      isClosed: convo.closed,
      updateAllChannels: () => {},
      isGroup: convo.isGroup,
      scrollToBottom,
      canAddNotes,
      startRecording,
      stopRecording,
      recordingBlob,
      isRecording,
      recordingTime,
      clearRecord,
      alert: null
    }),
    [
      type,
      onTypeSet,
      isDirect,
      checkSubmit,
      disabled,
      ownerDeleted,
      allTags,
      createTask,
      session,
      convo.closed,
      convo.isGroup,
      scrollToBottom,
      canAddNotes,
      startRecording,
      stopRecording,
      recordingBlob,
      isRecording,
      recordingTime,
      clearRecord
    ]
  )

  const actionProps = React.useMemo(
    () => ({
      type,
      onTypeSet,
      sendMessage,
      disabled:
        isRecording ||
        disabled ||
        ownerDeleted ||
        (type !== 'Task' && !message.length && !file.length),
      createTask,
      doneUploading,
      session,
      canAddNotes
    }),
    [
      type,
      onTypeSet,
      sendMessage,
      disabled,
      ownerDeleted,
      message.length,
      file.length,
      createTask,
      doneUploading,
      session,
      canAddNotes,
      isRecording
    ]
  )

  const removeFile = React.useCallback(
    i => {
      setFile(f => {
        const files = [...f]
        files.splice(i, 1)
        return files
      })
    },
    [setFile]
  )

  const SendButton = React.useCallback(
    props => (
      <Actions {...actionProps} {...props}>
        <SendControl />
      </Actions>
    ),
    [actionProps]
  )

  return (
    <>
      <div className={classes.root}>
        <TextArea {...textAreaProps} SendButton={SendButton}>
          {file?.length > 0 && (
            <div className={classes.files}>
              {file.map(
                (f, i) =>
                  f?.name && (
                    <AttachedFile
                      key={i}
                      file={f}
                      onDelete={() => removeFile(i)}
                    />
                  )
              )}
            </div>
          )}
        </TextArea>
      </div>
    </>
  )
}

export default Controls
